import {FC, useEffect, useMemo, useState} from 'react';
//formik
import {useFormikContext} from 'formik';
//mui
import {Box} from '@mui/material';
//icons
// import {ReactComponent as PaypalIcon} from '@/assets/icons/paypal-logo.svg';
import {ReactComponent as StripeIcon} from '@/assets/icons/stripe-logo.svg';
// import {ReactComponent as SquareIcon} from '@/assets/icons/square-logo.svg';
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';
//constants
import {COMPLETE, FILLED, TEXT} from '@/utils/constants';
//components
import Button from '@/shared/components/Button';
import Modal from '@/shared/components/Modal';
import HeaderFormContent from '../../../HeaderForm';
import StepperActions from '../StepperActions';
import ModalInfoDescription from './ModalInfoDescription';
//form configuration
import {SignUpFormValues} from '../formConfiguration';
//styles
import {paymentContentStyle} from './PaymentContent.styles';
import { toast } from 'react-toastify';
import { OrganizationStripeService } from '@/utils/services/organization-stripe.services';
import { useAppSelector } from '@/store/hooks';
import { getOrganizationIdSelector } from '@/store/organization';
import { getErrorMessage } from '@/utils/helpers/error';

const PaymentContent: FC = () => {
	const {submitForm} = useFormikContext<SignUpFormValues>();

	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

	//TODO: handle with BE when BE implement logic
	const [isConnectPaypal, setIsConnectPaypal] = useState(false);
	const [isConnectStripe, setIsConnectStripe] = useState(false);
	const [isConnectSquare, setIsConnectSquare] = useState(false);
	const [isStripeLoading, setIsStripeLoading] = useState<boolean>(false);

	const organizationId = useAppSelector(getOrganizationIdSelector);

	const handlePaypalConnect = () => setIsConnectPaypal(true);
	const handleStripeConnect = () => {
		if (isConnectStripe) return;
		startStripeOnboarding();
	};


	const handleSquareConnect = () => setIsConnectSquare(true);

		const getStripeSettings = async () => {
		if (!organizationId) return;
		try {
			const response = await OrganizationStripeService.getOrganizationStripePaymentSettings(organizationId);
			const data = response.data;
			const isConnected = !!(data.stripeDetailsSubmitted && data.stripeChargesEnabled && data.stripeConnectedAccountId);
			setIsConnectStripe(isConnected);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

		const startStripeOnboarding = async () => {
		try {
			setIsStripeLoading(true);
			const toastId = toast.loading('Preparing Stripe onboarding...');

			const data={
             isRegistationFlow: true
		    }

			const response = await OrganizationStripeService.createStrapiOnboardingLink(organizationId!, data);

			if (response.data?.url) {
				toast.dismiss(toastId);
				toast.success('Redirecting to Stripe...');
				window.location.href = response.data.url;
			} else {
				throw new Error('Onboarding URL not found in response');
			}
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsStripeLoading(false);
		}
	};

	const isNextStepActive = useMemo(
		() => isConnectPaypal || isConnectStripe || isConnectSquare,
		[isConnectPaypal, isConnectStripe, isConnectSquare]
	);

	const nextClickHandler = () => submitForm();

	const onOpenHelpModal = () => setIsHelpModalOpen(!isHelpModalOpen);

	const paypalTitle = useMemo(() => (isConnectPaypal ? 'Disconnect Paypal' : 'Connect with Paypal'), [isConnectPaypal]);
	const stripeTitle = useMemo(
		() => (isStripeLoading ? 'Connecting...' : isConnectStripe ? 'Stripe Connected' : 'Connect with Stripe'),
		[isConnectStripe, isStripeLoading]
	);
	const squareTitle = useMemo(() => (isConnectSquare ? 'Disconnect Square' : 'Connect with Square'), [isConnectSquare]);

	useEffect(() => {
		if (!organizationId) return;

		const params = new URLSearchParams(window.location.search);
		const cleanUrlParams = () => {
			const url = new URL(window.location.href);
			url.searchParams.delete('stripeReturn');
			url.searchParams.delete('restartStripe');
			window.history.replaceState({}, document.title, url.pathname + url.search);
		};

		if (params.get('stripeReturn') === '1') {
			(async () => {
				try {
					setIsStripeLoading(true);
					await OrganizationStripeService.syncOrganizationStripeConnection(organizationId);
					await getStripeSettings();
					toast.success('Stripe connected successfully!');
					cleanUrlParams();
				} catch (error) {
					toast.error(getErrorMessage(error));
				} finally {
					setIsStripeLoading(false);
				}
			})();
		} else if (params.get('restartStripe') === '1') {
			cleanUrlParams();
			startStripeOnboarding();
		} else {
			getStripeSettings();
		}
	}, [organizationId]);


	return (
		<Box sx={paymentContentStyle.wrapper}>
			<HeaderFormContent
				title="Payment Processor"
				description="Connect your preferred payment processor to collect payments. Payments (minus transaction fees) are deposited directly into your account."
			/>

			<Box sx={paymentContentStyle.buttons}>
				{/* <Button
					onClick={handlePaypalConnect}
					title={paypalTitle}
					startIcon={<PaypalIcon />}
					sx={paymentContentStyle.buttonPaypal}
					disableRipple
				/> */}
				<Button
					onClick={handleStripeConnect}
					title={stripeTitle}
					startIcon={<StripeIcon />}
					sx={paymentContentStyle.buttonStripe}
					disableRipple
					disabled={isStripeLoading || isConnectStripe}
				/>
				{/* <Button
					onClick={handleSquareConnect}
					title={squareTitle}
					startIcon={<SquareIcon />}
					sx={paymentContentStyle.buttonSquare}
					disableRipple
				/> */}
			</Box>

			<Button
				onClick={onOpenHelpModal}
				title="Need help connecting a payment option?"
				variant={TEXT}
				sx={paymentContentStyle.buttonHelp}
			/>

			<Modal
				title="Need Help Connecting a Payment Option?"
				mainIcon={<QuestionIcon />}
				open={isHelpModalOpen}
				onClose={onOpenHelpModal}
				isShowCloseButton
				closeButtonVariant={FILLED}
				sx={paymentContentStyle.modal}
			>
				<ModalInfoDescription />
			</Modal>

			<StepperActions
				title={COMPLETE}
				nextClickHandler={nextClickHandler}
				skipClickHandler={nextClickHandler}
				nextDisabled={!isNextStepActive}
			/>
		</Box>
	);
};

export default PaymentContent;
